import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  DatePicker,
  Flex,
  Image,
  App,
  Row,
  Col,
  Select,
  Radio,
  InputNumber,
  Button,
} from "antd";
import DatePickerIcon from "../../assets/icons/datepicker-calendar.svg";
import { ArrowDownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "../../config/httpConfig";
import { downloadBlob, getDownloadSuccessMessage } from "../../helpers/helpers";
import DownloadFormatDropdown from "../../components/DownloadFormatDropdown/DownloadFormatDropdown";
import "./Reports.scss";

const { Option } = Select;
const { Text } = Typography;

const filtersByTitle = {
  "Deal wise Settlement": [
    "timePeriod",
    "enterpriseName",
    "dealAmt",
    "settledAmt",
    "dealId",
  ],
  "Business Ledger": ["timePeriod", "txnAmt"],
};

const filterLabels = {
  timePeriod: "Time Period",
  enterpriseName: "Enterprise Name",
  dealId: "Deal Id",
  dealAmt: "Deal Amount",
  settledAmt: "Settled Amount",
  txnAmt: "Amount",
};

const ReportsCollapseContent = ({ type, title }) => {
  const { message } = App.useApp();
  const { reportsDropdownData } = useSelector((state) => state.reports);
  const [selectedFilter, setSelectedFilter] = useState("timePeriod");
  const [validationError, setValidationError] = useState("");
  const [selectedValue, setSelectedValue] = useState({
    startDate: null,
    endDate: null,
    enterpriseName: "",
    dealId: "",
    dealAmtMin: "",
    dealAmtMax: "",
    settledAmtMin: "",
    settledAmtMax: "",
    txnAmtMin: "",
    txnAmtMax: "",
  });

  const isDateRangeIncomplete =
    selectedFilter === "timePeriod" &&
    (!selectedValue.startDate || !selectedValue.endDate);

  const isRangeIncomplete = ["dealAmt", "txnAmt", "settledAmt"].includes(
    selectedFilter,
  )
    ? !selectedValue[`${selectedFilter}Min`] ||
      !selectedValue[`${selectedFilter}Max`]
    : false;

  const handleInputChange = (name, value) => {
    setSelectedValue((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  const validateRange = (min, max, label) => {
    if (isEmpty(min) || isEmpty(max)) {
      setValidationError("");
      return;
    }

    if (Number(min) > Number(max)) {
      setValidationError(
        `Minimum ${label} cannot be greater than Maximum ${label}`,
      );
    } else {
      setValidationError("");
    }
  };

  const handleAmountKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];

    // Allow Ctrl/Cmd shortcuts
    if (
      (e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x"].includes(e.key.toLowerCase())
    ) {
      return;
    }

    const isNumber = /^[0-9]$/.test(e.key);

    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleAmountPaste = (e) => {
    const pasteValue = e.clipboardData.getData("text");

    if (!/^\d+$/.test(pasteValue)) {
      e.preventDefault();
    }
  };

  const downloadReport = async (format, type) => {
    try {
      let endPoint = "";

      if (type === "1") {
        endPoint = "dealWiseSettlement";
      } else {
        endPoint = "businessLedger";
      }

      const filters = {
        format,
        startDate: selectedValue?.startDate,
        endDate: selectedValue?.endDate,
        enterpriseName: selectedValue?.enterpriseName,
        dealAmt: {
          min: selectedValue?.dealAmtMin,
          max: selectedValue?.dealAmtMax,
        },
        settledAmt: {
          min: selectedValue?.settledAmtMin,
          max: selectedValue?.settledAmtMax,
        },
        txnAmt: {
          min: selectedValue?.txnAmtMin,
          max: selectedValue?.txnAmtMax,
        },
        dealId: selectedValue?.dealId,
      };

      const cleanFilters = (filters) => {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters)
            .map(([key, value]) => {
              if (typeof value === "object" && value !== null) {
                const cleanedValue = cleanFilters(value);
                if (Object.keys(cleanedValue).length === 0) {
                  return null;
                }
                return [key, cleanedValue];
              }
              return value !== undefined && value !== "" && value !== null
                ? [key, value]
                : null;
            })
            .filter(Boolean),
        );
        return cleanedFilters;
      };

      const filteredParams = cleanFilters(filters);

      const response = await axios.get(`/api/business/${endPoint}`, {
        params: { ...filteredParams },
        responseType: "blob",
      });

      const contentType = response.type;

      // If it's not a valid file (e.g. error JSON), parse it and show message
      if (contentType === "application/json") {
        const text = await response.text();
        const result = JSON.parse(text);
        throw new Error(result?.response?.message || "No data found");
      }

      // ✅ Valid blob, proceed with download
      const fileName =
        type === "1" ? "Deal wise Settlement" : "Business Ledger";
      const downloadedFileName = `${fileName}.${format === "pdf" ? "pdf" : "xlsx"}`;
      downloadBlob(response, downloadedFileName);
      message.success(getDownloadSuccessMessage(downloadedFileName));
    } catch (error) {
      console.error("Error downloading report:", error);
      message.error(
        error.message ||
          "Failed to download the report. Please try again later.",
      );
    }
  };

  const disableFutureDates = (current) =>
    current && current > dayjs().endOf("day");

  const renderFilterField = () => {
    switch (selectedFilter) {
      case "timePeriod":
        return (
          <Flex gap={10}>
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="Start Date"
              value={
                selectedValue.startDate ? dayjs(selectedValue.startDate) : null
              }
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              onChange={(date) => {
                handleInputChange(
                  "startDate",
                  date ? date.format("YYYY-MM-DD") : null,
                );

                handleInputChange("endDate", null);
              }}
              disabledDate={disableFutureDates}
              style={{ width: "100%" }}
            />
            <DatePicker
              format="DD/MM/YYYY"
              placeholder="End Date"
              value={
                selectedValue.endDate ? dayjs(selectedValue.endDate) : null
              }
              suffixIcon={
                <Image src={DatePickerIcon} alt="Calendar" preview={false} />
              }
              onChange={(date) =>
                handleInputChange(
                  "endDate",
                  date ? date.format("YYYY-MM-DD") : null,
                )
              }
              disabledDate={(current) => {
                if (current && current > dayjs().endOf("day")) {
                  return true;
                }

                if (selectedValue.startDate) {
                  return current.isBefore(
                    dayjs(selectedValue.startDate),
                    "day",
                  );
                }

                return false;
              }}
              style={{ width: "100%" }}
            />
          </Flex>
        );
      case "dealAmt":
      case "txnAmt":
      case "settledAmt":
        return (
          <Flex gap={20}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Min ${filterLabels[selectedFilter]}`}
              value={selectedValue[`${selectedFilter}Min`] || null}
              changeOnWheel={false}
              controls={false}
              maxLength={15}
              formatter={(value) =>
                value
                  ? new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 0,
                    }).format(value)
                  : ""
              }
              parser={(value) => value?.replace(/\D/g, "")}
              onKeyDown={handleAmountKeyDown}
              onPaste={handleAmountPaste}
              onChange={(value) => {
                handleInputChange(`${selectedFilter}Min`, value ?? "");

                validateRange(
                  value,
                  selectedValue[`${selectedFilter}Max`],
                  filterLabels[selectedFilter],
                );
              }}
            />
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Max ${filterLabels[selectedFilter]}`}
              value={selectedValue[`${selectedFilter}Max`] || null}
              changeOnWheel={false}
              controls={false}
              maxLength={15}
              formatter={(value) =>
                value
                  ? new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 0,
                    }).format(value)
                  : ""
              }
              parser={(value) => value?.replace(/\D/g, "")}
              onKeyDown={handleAmountKeyDown}
              onPaste={handleAmountPaste}
              onChange={(value) => {
                handleInputChange(`${selectedFilter}Max`, value ?? "");

                validateRange(
                  selectedValue[`${selectedFilter}Min`],
                  value,
                  filterLabels[selectedFilter],
                );
              }}
            />
          </Flex>
        );
      default:
        return (
          <Select
            style={{ width: "100%" }}
            placeholder={`Select ${filterLabels[selectedFilter]}`}
            value={selectedValue[selectedFilter] || undefined}
            onChange={(value) => handleInputChange(selectedFilter, value)}
          >
            {reportsDropdownData[selectedFilter]?.map((option, index) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
    }
  };

  return (
    <div className="collapse-item-content-container">
      <Row gutter={[16, 16]} align="middle" wrap>
        <Col span={24}>
          <Radio.Group
            value={selectedFilter}
            onChange={(e) => {
              setSelectedFilter(e.target.value);
              setSelectedValue({});
              setValidationError("");
            }}
            options={filtersByTitle[title]?.map((filter) => ({
              label: filterLabels[filter],
              value: filter,
            }))}
          />
        </Col>
        <Col xs={24} md={10} lg={4}>
          <Text style={{ color: "#676767", fontSize: 14 }}>
            {filterLabels[selectedFilter]}
          </Text>
        </Col>
        <Col xs={24} md={24} lg={14}>
          {renderFilterField()}
        </Col>
        <Col xs={24} md={24} lg={6} style={{ textAlign: "right" }}>
          <DownloadFormatDropdown
            onPdf={() => downloadReport("pdf", type)}
            onCsv={() => downloadReport("xlsx", type)}
            placement="bottomCenter"
            arrow={{ pointAtCenter: true }}
            overlayStyle={{
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            trigger={
              <Button
                variant="solid"
                color="primary"
                icon={<ArrowDownOutlined />}
                iconPosition="end"
                style={{ padding: 24 }}
                disabled={
                  isDateRangeIncomplete ||
                  (["dealAmt", "txnAmt", "settledAmt"].includes(
                    selectedFilter,
                  ) &&
                    (validationError || isRangeIncomplete))
                }
              >
                Download
              </Button>
            }
          />
        </Col>
      </Row>
      {validationError && (
        <Text style={{ color: "red", fontSize: 12 }}>{validationError}</Text>
      )}
    </div>
  );
};

export default ReportsCollapseContent;
